import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button, Input, Skeleton, StatusBadge, RowActions, RadixSelect, Tabs, TabsList, TabsTrigger, Pagination,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  type SelectOption,
} from '@/components/ui';
import { Plus, ImageOff } from 'lucide-react';
import { useHomeBannersQuery, useHomeBannerMutation } from '../hooks/useHomeBannersQuery';
import { bannerScheduleState, isBannerLive } from '../mainPage/lib/bannerSchedule';
import {
  applyPageReorder, canDragToReorder, moveRowToEdge, moveRowToPosition, positionOf, selectBannerRows,
  type BannerScope, type BannerSortKey, type MoveEdge,
} from '../mainPage/lib/bannerListView';
import { SortableList } from './SortableList';

/**
 * 서버에서는 **전량을 한 번에** 받는다. 드래그 정렬이 전체 순서를 통째로 영속하고(BE reorder 는
 * 받은 배열의 인덱스를 sort_order 로 쓴다) 탭의 건수 표기도 전체를 알아야 정확하기 때문이다.
 * 화면 페이징은 이 전량 위에서 클라이언트가 자른다.
 */
const FETCH_SIZE = 200;

/**
 * 화면에 한 번에 보여줄 행 수.
 *
 * ★"전체 보기"(페이징 끄기)는 일부러 두지 않는다 — 배너마다 썸네일 이미지가 붙어 있어 수백 건을
 * 한 화면에 펼치면 렌더가 무거워진다. 페이지를 넘나드는 순서 변경은 행 메뉴의
 * "맨 위로/맨 아래로/위치 지정 이동"으로 처리한다(전체 목록을 렌더하지 않고도 전량 순서를 다시 보낸다).
 */
const ROWS_PER_PAGE_OPTIONS = [20, 50] as const;
type RowsPerPage = (typeof ROWS_PER_PAGE_OPTIONS)[number];
const DEFAULT_ROWS_PER_PAGE: RowsPerPage = 20;

type HomeBannerRow = {
  id: number;
  albumTitle: string;
  artistName: string;
  bannerKey: string;
  sortOrder: number;
  isActive: boolean;
  imageUrl?: string | null;
  // ID45 노출 예약 윈도우 — 활성이어도 이 구간 밖이면 스토어프론트에 노출되지 않는다.
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  updatedAt?: string | null;
};

export interface HomeBannerListPanelProps {
  /** Optional toolbar slot (e.g. the "배너 등록" button) rendered above the table. */
  toolbar?: ReactNode;
}

/**
 * Hero 배너 목록 본문(드래그 정렬 + 삭제 다이얼로그). PageShell 을 포함하지 않으므로
 * standalone 페이지(HomeBannerListPage)와 메인 페이지 관리 탭에서 함께 재사용한다.
 *
 * ID45: 행 좌측 그립을 드래그하면 순서를 바꾸고, drop 시 `PUT /admin/v1/home-banners/reorder`
 * 로 새 순서(bannerIds 배열)를 영속한다. 서버 재조회 전까지 낙관적 로컬 순서로 표시한다.
 */
export const HomeBannerListPanel = ({ toolbar }: HomeBannerListPanelProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const query = useHomeBannersQuery(0, FETCH_SIZE);
  const { remove, reorder } = useHomeBannerMutation();
  const [deleteTarget, setDeleteTarget] = useState<HomeBannerRow | null>(null);
  // 위치 지정 이동 다이얼로그 — 대상 행과 입력 중인 순번(문자열 그대로 두어야 지우고 다시 칠 수 있다).
  const [moveTarget, setMoveTarget] = useState<HomeBannerRow | null>(null);
  const [movePosition, setMovePosition] = useState('');

  // 서버 순서를 로컬에 미러 — 드래그 즉시 반영(낙관적), 재조회 시 동기화.
  const [rows, setRows] = useState<HomeBannerRow[]>([]);
  useEffect(() => {
    if (query.data?.content) setRows(query.data.content as HomeBannerRow[]);
  }, [query.data]);

  // 기본은 '전체' — 이 패널의 기존 주 작업이 드래그 정렬인데 그건 전체 범위에서만 안전하다.
  // '노출 중' 은 "지금 뭐가 걸려 있나" 를 확인하는 용도의 opt-in 뷰.
  const [scope, setScope] = useState<BannerScope>('all');
  const [sortKey, setSortKey] = useState<BannerSortKey>('sortOrder');

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(DEFAULT_ROWS_PER_PAGE);
  // 범위·정렬·페이지 크기가 바뀌면 보던 페이지 번호는 의미가 없어진다.
  useEffect(() => setPage(1), [scope, sortKey, rowsPerPage]);

  const liveCount = useMemo(() => rows.filter((r) => isBannerLive(r)).length, [rows]);
  const canDrag = canDragToReorder(scope, sortKey);

  /** 범위·정렬을 적용한 **전체** 목록. 페이지는 이 배열에서 잘라낸다. */
  const orderedRows = useMemo(
    () => selectBannerRows(rows, scope, sortKey),
    [rows, scope, sortKey],
  );
  /**
   * 순서 변경의 기준이 되는 **전체 순서**. 화면이 '노출 중' 탭이거나 다른 기준으로 정렬돼 있어도
   * 순번의 의미는 언제나 이 배열 기준이다 — BE 로는 항상 전량을 보내기 때문이다.
   */
  const orderedAll = useMemo(() => selectBannerRows(rows, 'all', 'sortOrder'), [rows]);

  const pageStart = (page - 1) * rowsPerPage;
  const visibleRows = useMemo(
    () => orderedRows.slice(pageStart, pageStart + rowsPerPage),
    [orderedRows, pageStart, rowsPerPage],
  );

  // 전량 로드 전제가 깨지면(배너가 FETCH_SIZE 초과) 순서·건수가 부분만 반영된다 — 조용히 틀리지 않게 알린다.
  const truncated = (query.data?.totalElements ?? 0) > rows.length;

  const sortOptions: ReadonlyArray<SelectOption> = [
    { value: 'sortOrder', label: t('cms.banners.sort.sortOrder', { defaultValue: '노출 순서' }) },
    { value: 'startAt', label: t('cms.banners.sort.startAt', { defaultValue: '노출 시작일 순' }) },
    { value: 'endAt', label: t('cms.banners.sort.endAt', { defaultValue: '노출 종료일 순' }) },
    { value: 'updated', label: t('cms.banners.sort.updated', { defaultValue: '최근 수정순' }) },
  ];

  const rowsPerPageOptions: ReadonlyArray<SelectOption> = ROWS_PER_PAGE_OPTIONS.map((n) => ({
    value: String(n),
    label: t('cms.banners.rowsPerPage.n', { count: n, defaultValue: '{{count}}개씩' }),
  }));

  /**
   * 드래그 결과 반영. SortableList 는 **현재 페이지**의 새 순서를 준다.
   * 그대로 보내면 페이지 밖 배너의 sort_order 와 충돌하므로, 전체 순서에 페이지 구간만
   * 교체한 배열을 만들어 전량을 보낸다(applyPageReorder).
   */
  const persistOrder = (nextAll: HomeBannerRow[]): void => {
    setRows(nextAll); // 낙관적 표시 — sortOrder 도 새 인덱스로 맞춰져 재정렬에 튕기지 않는다.
    reorder.mutate(
      nextAll.map((r) => r.id),
      { onError: () => { if (query.data?.content) setRows(query.data.content as HomeBannerRow[]); } },
    );
  };

  const handleReorder = (reorderedPage: HomeBannerRow[]): void => {
    persistOrder(applyPageReorder(orderedRows, pageStart, reorderedPage));
  };

  /**
   * 페이지를 넘나드는 이동. 드래그는 한 페이지 안에서만 되므로 행 메뉴로 처리한다 —
   * 흔한 의도인 "맨 위로 / 맨 아래로" 는 한 번에, 그 밖의 자리는 "위치 지정 이동" 으로.
   * 노출 순서 정렬일 때만 의미가 있고(다른 기준으로 정렬 중이면 "위"·"7번째" 가 무엇인지
   * 모호하다), 범위와 무관하게 항상 **전체 순서** 기준으로 옮긴다 — 보내는 건 언제나 전량이라
   * 안전하다.
   */
  const canMoveAcrossPages = sortKey === 'sortOrder';
  const handleMoveToEdge = (id: number, edge: MoveEdge): void => {
    persistOrder(moveRowToEdge(orderedAll, id, edge));
  };

  const movingFrom = moveTarget ? positionOf(orderedAll, moveTarget.id) : null;

  const openMovePosition = (row: HomeBannerRow): void => {
    setMoveTarget(row);
    setMovePosition(String(positionOf(orderedAll, row.id) ?? 1));
  };

  /** 입력이 유효할 때만 순번(1-based), 아니면 null — 버튼 활성화와 오류 문구를 이 하나로 판단한다. */
  const parsedMovePosition = useMemo(() => {
    const raw = movePosition.trim();
    if (!/^[0-9]+$/.test(raw)) return null;
    const n = Number(raw);
    return n >= 1 && n <= orderedAll.length ? n : null;
  }, [movePosition, orderedAll.length]);

  const submitMovePosition = (): void => {
    if (!moveTarget || parsedMovePosition === null) return;
    persistOrder(moveRowToPosition(orderedAll, moveTarget.id, parsedMovePosition));
    // 옮긴 자리가 다른 페이지면 결과가 화면 밖으로 나간다 — 그 페이지로 따라가 이동을 눈으로 확인시킨다.
    // (전체 순번과 화면 순서가 일치하는 조합에서만. 그 외에는 지금 페이지를 유지한다.)
    if (scope === 'all' && sortKey === 'sortOrder') {
      setPage(Math.max(1, Math.ceil(parsedMovePosition / rowsPerPage)));
    }
    setMoveTarget(null);
  };

  return (
    <>
      {toolbar ? <div className="mb-3 flex justify-end">{toolbar}</div> : null}

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[52px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('cms.banners.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('cms.retry')}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
        >
          {t('cms.banners.empty')}
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <Tabs value={scope} onValueChange={(v) => setScope(v as BannerScope)}>
              <TabsList>
                <TabsTrigger value="live">
                  {t('cms.banners.scope.live', { defaultValue: '노출 중' })} ({liveCount})
                </TabsTrigger>
                <TabsTrigger value="all">
                  {t('cms.banners.scope.all', { defaultValue: '전체' })} ({rows.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <div className="w-[160px]">
                <RadixSelect
                  value={sortKey}
                  onChange={(v) => setSortKey(v as BannerSortKey)}
                  options={sortOptions}
                  aria-label={t('cms.banners.sort.label', { defaultValue: '정렬 기준' })}
                />
              </div>
              <div className="w-[130px]">
                <RadixSelect
                  value={String(rowsPerPage)}
                  onChange={(v) => setRowsPerPage(Number(v) as RowsPerPage)}
                  options={rowsPerPageOptions}
                  aria-label={t('cms.banners.rowsPerPage.label', { defaultValue: '페이지당 행 수' })}
                />
              </div>
            </div>
          </div>

          {truncated ? (
            <p
              role="alert"
              className="mb-2 rounded-md p-2 text-xs"
              style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
            >
              {t('cms.banners.truncated', {
                total: query.data?.totalElements ?? 0,
                loaded: rows.length,
                defaultValue:
                  '배너가 {{total}}건인데 {{loaded}}건만 불러왔습니다. 건수와 순서 변경이 일부만 반영되므로 담당자에게 알려주세요.',
              })}
            </p>
          ) : null}

          <p className="mb-2 text-xs" style={{ color: 'var(--content-muted)' }}>
            {canDrag
              ? t('cms.banners.dragHintPaged', {
                  defaultValue:
                    '행 왼쪽 손잡이를 드래그하여 순서를 변경합니다. 드래그는 현재 페이지 안에서만 가능합니다 — 다른 페이지로 보내려면 행 메뉴의 "맨 위로/맨 아래로/위치 지정 이동"을 쓰세요.',
                })
              : t('cms.banners.dragDisabledHint', {
                  defaultValue:
                    '순서 변경은 "전체" 탭 + "노출 순서" 정렬에서만 가능합니다. 일부만 보거나 다른 기준으로 정렬한 상태에서 순서를 바꾸면 화면에 없는 배너와 순서가 어긋납니다.',
                })}
          </p>

          {visibleRows.length === 0 ? (
            <div
              className="rounded-lg py-12 text-center text-sm"
              style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
            >
              {t('cms.banners.emptyLive', {
                defaultValue: '지금 노출 중인 배너가 없습니다. "전체" 탭에서 노출 기간과 활성 여부를 확인하세요.',
              })}
            </div>
          ) : (
          <SortableList
            items={visibleRows}
            getId={(row) => row.id}
            onReorder={handleReorder}
            disabled={!canDrag || reorder.isPending}
            handleLabel={t('cms.banners.dragHandleLabel', { defaultValue: '배너 순서 이동 핸들' })}
            className="flex flex-col gap-1.5"
          >
            {(row, _index, dragHandle) => (
              <div
                className="flex items-center gap-2 rounded-md px-2 py-2"
                style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
              >
                {dragHandle}
                <div
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded"
                  style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
                >
                  {row.imageUrl ? (
                    <img src={row.imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <ImageOff size={14} style={{ color: 'var(--content-soft)' }} aria-hidden="true" />
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span
                    className="truncate text-sm font-medium"
                    style={{ color: 'var(--content-strong)' }}
                    title={row.albumTitle}
                  >
                    {row.albumTitle}
                  </span>
                  <span
                    className="truncate text-xs"
                    style={{ color: 'var(--content-muted)' }}
                    title={row.artistName}
                  >
                    {row.artistName}
                  </span>
                </div>
                <StatusBadge
                  variant={row.isActive ? 'success' : 'info'}
                  label={row.isActive ? t('cms.active') : t('cms.inactive')}
                />
                {/* 활성인데 노출 예약 윈도우 밖이면 그 사유를 같이 보여준다 — 이게 없으면
                    "활성 배너는 많은데 미리보기엔 몇 개 안 뜬다"의 원인을 화면에서 알 수 없다. */}
                {row.isActive && bannerScheduleState(row) !== 'live' ? (
                  <StatusBadge
                    variant={bannerScheduleState(row) === 'scheduled' ? 'info' : 'warning'}
                    label={
                      bannerScheduleState(row) === 'scheduled'
                        ? t('cms.banners.scheduleScheduled', { defaultValue: '노출 예정' })
                        : t('cms.banners.scheduleEnded', { defaultValue: '기간 종료' })
                    }
                  />
                ) : null}
                <RowActions
                  triggerLabel={t('cms.banners.rowActionsLabel', { defaultValue: '배너 액션' })}
                  actions={[
                    {
                      key: 'edit',
                      label: t('cms.edit'),
                      onClick: () => navigate(`/cms/home-banners/${row.id}`),
                    },
                    // 페이지를 넘나드는 이동 수단 — 드래그로는 현재 페이지를 벗어날 수 없다.
                    ...(canMoveAcrossPages
                      ? [
                          {
                            key: 'move-top',
                            label: t('cms.banners.moveTop', { defaultValue: '맨 위로' }),
                            separatorBefore: true,
                            disabled: reorder.isPending,
                            onClick: () => handleMoveToEdge(row.id, 'top'),
                          },
                          {
                            key: 'move-bottom',
                            label: t('cms.banners.moveBottom', { defaultValue: '맨 아래로' }),
                            disabled: reorder.isPending,
                            onClick: () => handleMoveToEdge(row.id, 'bottom'),
                          },
                          {
                            key: 'move-position',
                            label: t('cms.banners.movePosition', { defaultValue: '위치 지정 이동…' }),
                            disabled: reorder.isPending,
                            onClick: () => openMovePosition(row),
                          },
                        ]
                      : []),
                    {
                      key: 'delete',
                      label: t('cms.delete'),
                      variant: 'danger',
                      separatorBefore: true,
                      onClick: () => setDeleteTarget(row),
                    },
                  ]}
                />
              </div>
            )}
          </SortableList>
          )}

          {orderedRows.length > 0 ? (
            <Pagination
              total={orderedRows.length}
              currentPage={page}
              pageSize={rowsPerPage}
              onPageChange={setPage}
              className="mt-4"
            />
          ) : null}
        </>
      )}

      {/* 위치 지정 이동 — 드래그(페이지 안)로도 맨 위/아래로도 닿지 않는 자리로 보낸다.
          순번은 화면의 행 번호가 아니라 **전체 순서** 기준이라, 현재 순번과 총 건수를 함께 보여준다. */}
      <Dialog open={moveTarget !== null} onOpenChange={(open) => { if (!open) setMoveTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('cms.banners.movePositionTitle', { defaultValue: '위치 지정 이동' })}
            </DialogTitle>
            <DialogDescription>
              {t('cms.banners.movePositionDesc', {
                title: moveTarget?.albumTitle ?? '',
                current: movingFrom ?? 0,
                total: orderedAll.length,
                defaultValue:
                  '"{{title}}"은(는) 전체 {{total}}건 중 현재 {{current}}번째입니다. 옮길 순번을 입력하세요.',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label
              htmlFor="banner-move-position"
              className="mb-1.5 block text-xs"
              style={{ color: 'var(--content-muted)' }}
            >
              {t('cms.banners.movePositionLabel', {
                total: orderedAll.length,
                defaultValue: '옮길 순번 (1 ~ {{total}})',
              })}
            </label>
            <Input
              id="banner-move-position"
              type="number"
              inputMode="numeric"
              min={1}
              max={orderedAll.length}
              step={1}
              autoFocus
              value={movePosition}
              onChange={(e) => setMovePosition(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitMovePosition();
                }
              }}
            />
            {movePosition.trim() !== '' && parsedMovePosition === null ? (
              <p role="alert" className="mt-1.5 text-xs" style={{ color: 'var(--status-danger-fg)' }}>
                {t('cms.banners.movePositionInvalid', {
                  total: orderedAll.length,
                  defaultValue: '1 부터 {{total}} 사이의 숫자를 입력하세요.',
                })}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              size="sm"
              disabled={parsedMovePosition === null || reorder.isPending}
              onClick={submitMovePosition}
            >
              {t('cms.banners.movePositionSubmit', { defaultValue: '이동' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.banners.title')} 삭제</DialogTitle>
            <DialogDescription>
              {t('cms.banners.confirmDelete', { title: deleteTarget?.albumTitle ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              {t('cms.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** "배너 등록" 버튼 — standalone 페이지·탭 양쪽 toolbar 에서 재사용. */
export const HomeBannerCreateButton = () => {
  const { t } = useTranslation('common');
  return (
    <Link to="/cms/home-banners/new">
      <Button size="sm">
        <Plus size={15} aria-hidden="true" />
        {t('cms.banners.create')}
      </Button>
    </Link>
  );
};
